import { LocalDate } from "@js-joda/core";
import { Text } from "components/Typography/text.styled";
import { Color } from "config/colors";
import { FontFamily, FontSize } from "config/font";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "services/translation/i18n";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { ActionDropdownButton } from "../general/actionDropdownButton/actionDropdownButton.component";
import { InfoModal } from "../general/infoModal/infoModal.component";
import {
  ArrowSign,
  DividerLine1,
  ExportCell,
  ExportContainerLeft,
  ExportContainerRight,
  ExportGrid,
  MonthSelectContainer,
  Row,
  SalaryPageContainer,
  TooltipContainer,
  TooltipModal,
} from "./salary.styled";
import { usePDFViewer } from "hooks/usePDFViewer";

export const handlePreviousYear = (setCurrentDate: any) => {
  return setCurrentDate((prevState: any) => prevState.minusYears(1));
};

export const handleNextYear = (setCurrentDate: any) => {
  const currentYear = LocalDate.now().year();

  return setCurrentDate((prevState: any) => {
    const next = prevState.plusYears(1);
    return next.year() > currentYear ? prevState : next;
  });
};

const AGITooltip = () => {
  const { t } = useTranslation();
  const [showInfo, setShowInfo] = useState(false);

  const handleMouseEnter = useCallback(() => {
    setShowInfo(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setShowInfo(false);
  }, []);

  return (
    <TooltipContainer>
      <Row
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ cursor: "pointer" }}
      >
        <Text
          fontFamily={FontFamily.MontserratSemiBold}
          fontSize={FontSize.Small}
        >
          {t(`Salary.WhatIs`, {
            name: t("Salary.SalaryExports").toLowerCase(),
          })}
        </Text>
      </Row>

      <TooltipModal
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          transform: showInfo ? "translateY(0)" : "translateY(-10px)",
          opacity: showInfo ? 1 : 0,
          visibility: showInfo ? "visible" : "hidden",
          pointerEvents: showInfo ? "auto" : "none",
          transition: "all 0.2s ease-in-out",
        }}
      >
        <Text fontSize={FontSize.Small}>{t("Salary.SalaryExportsInfo")}</Text>
      </TooltipModal>
    </TooltipContainer>
  );
};

type MonthOption = { value: number; label: string };

export const SalaryExportsOverview = () => {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(LocalDate.now());
  const [{ company }] = useCompanyStore();
  const { prepareExport } = usePDFViewer();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showInfo, setShowInfo] = useState(false);

  const months: MonthOption[] = useMemo(
    () => [
      { value: 1, label: t("General.January") },
      { value: 2, label: t("General.February") },
      { value: 3, label: t("General.March") },
      { value: 4, label: t("General.April") },
      { value: 5, label: t("General.May") },
      { value: 6, label: t("General.June") },
      { value: 7, label: t("General.July") },
      { value: 8, label: t("General.August") },
      { value: 9, label: t("General.September") },
      { value: 10, label: t("General.October") },
      { value: 11, label: t("General.November") },
      { value: 12, label: t("General.December") },
    ],
    [t, i18n.language]
  );

  const formattedDate = useMemo(() => {
    return `${currentDate.year()}`;
  }, [currentDate]);

  const handlePrev = useCallback(() => {
    handlePreviousYear(setCurrentDate);
  }, [setCurrentDate]);

  const handleNext = useCallback(() => {
    handleNextYear(setCurrentDate);
  }, [setCurrentDate]);

  const handleExport = async (month: number, type: "SIE" | "Hogia") => {
    setErrorMessage("");
    try {
      setLoading(true);

      const companyId = company?.id;
      const year = currentDate.year();
      if (!companyId) {
        setLoading(false);
        return;
      }

      await prepareExport(month, type, year);

      setShowInfo(true);
    } catch (err: any) {
      setErrorMessage(err?.errorMessage || "NotAvailable");
      setShowInfo(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SalaryPageContainer style={{ marginBottom: 100 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
          marginBottom: 20,
          position: "relative",
        }}
      >
        <AGITooltip />
      </div>

      <MonthSelectContainer>
        <ArrowSign onClick={handlePrev}>&#60;</ArrowSign>
        <Text
          fontSize={FontSize.H4}
          fontFamily={FontFamily.MontserratSemiBold}
          style={{ margin: 0 }}
        >
          {formattedDate}
        </Text>
        {currentDate.plusYears(1).year() <= LocalDate.now().year() && (
          <ArrowSign onClick={handleNext}>&#62;</ArrowSign>
        )}
      </MonthSelectContainer>

      <ExportGrid>
        {months.map((month) => (
          <ExportCell key={month.value}>
            <ExportContainerLeft>
              <Text
                style={{ margin: 0, padding: 0 }}
                fontSize={FontSize.Small}
                color={Color.SeaBlue600}
              >
                {t("Salary.ExportPeriod")}
              </Text>

              <Text
                color={Color.MidnightBlue}
                fontFamily={FontFamily.MontserratRegular}
              >
                {month.label}
              </Text>
            </ExportContainerLeft>

            <DividerLine1 />

            <ExportContainerRight>
              <ActionDropdownButton
                disabled={loading}
                action
                size="medium"
                options={[
                  {
                    id: "hogia",
                    label: "Hogia",
                    onClick: () => handleExport(month.value, "Hogia"),
                  },
                  {
                    id: "SIE",
                    label: "SIE",
                    onClick: () => handleExport(month.value, "SIE"),
                  },
                ]}
              >
                {t("Salary.Export")}
              </ActionDropdownButton>
            </ExportContainerRight>
          </ExportCell>
        ))}
      </ExportGrid>

      {showInfo && (
        <InfoModal
          isOpen={showInfo}
          title={
            errorMessage !== ""
              ? t("Salary.NotAvailable")
              : t("Salary.Downloaded")
          }
          message={
            errorMessage !== ""
              ? t(`Salary.${errorMessage}`)
              : t("Salary.DownloadSuccess")
          }
          confirmText={t("OrganizationalStructure.Ok")}
          onConfirm={() => setShowInfo(false)}
          onCancel={() => setShowInfo(false)}
        />
      )}
    </SalaryPageContainer>
  );
};
